<template>
  <div class="timeslots-mobile">
    <div class="timeslots-mobile__calendar" ref="calendarRef">
      <div
        class="timeslots-mobile__calendar__week"
        v-for="(week, index) in dateBubbles"
        :key="index"
      >
        <button
          type="button"
          class="timeslots-mobile__calendar__date"
          v-for="date in week"
          :key="date"
          @click="handleSelectDate(date)"
          :aria-label="getDateAriaLabel(date)"
          :aria-selected="date === singleDate"
          :aria-disabled="!availableDates.includes(date)"
        >
          <div
            class="timeslots-mobile__calendar__date__digit"
            :class="{
              'timeslots-mobile__calendar__date__digit--available': availableDates.includes(date),
              'timeslots-mobile__calendar__date__digit--selected': date === singleDate,
            }"
          >
            {{ convertToDate(date) }}
          </div>
          <p class="timeslots-mobile__calendar__date__day">
            {{ convertToDay(date) }}
          </p>
        </button>
        <hr v-if="index !== dateBubbles.length - 1" />
      </div>
    </div>
    <div class="timeslots-mobile__selection" role="region" aria-label="Verfügbare Termine">
      <h3 class="timeslots-mobile__selection__header" id="date-selection-header">
        {{ convertDateHeader }}
      </h3>
      <fieldset class="timeslots-mobile__selection__body" aria-labelledby="date-selection-header">
        <legend class="sr-only">Verfügbare Termine für {{ convertDateHeader }}</legend>
        <template
          v-for="(timeslot, index) in clusteredTimeslots"
          :key="timeslot.id || timeslot[0].id"
        >
          <el-popover
            v-if="Array.isArray(timeslot)"
            trigger="click"
            :placement="index % 2 ? 'bottom-end' : 'bottom-start'"
            :offset="4"
            :teleported="false"
            width="calc(100% - 32px)"
            @show="activeDropdown = timeslot[0].id"
            @hide="activeDropdown === timeslot[0].id ? (activeDropdown = null) : null"
          >
            <template #reference>
              <button
                type="button"
                class="timeslots-mobile__selection__body__slot"
                :class="{
                  'timeslots-mobile__selection__body__slot--active':
                    activeDropdown === timeslot[0].id,
                  'timeslots-mobile__selection__body__slot--selected': isDropdownSelected(timeslot),
                }"
                :aria-expanded="activeDropdown === timeslot[0].id"
                :aria-haspopup="true"
              >
                <span class="timeslots-mobile__selection__body__slot--prefix"> ab </span>
                {{
                  isDropdownSelected(timeslot)
                    ? minutesToHHMM(selectedTimeslot[0].von)
                    : minutesToHHMM(timeslot[0].von)
                }}
                <IconChevronUp
                  v-if="activeDropdown === timeslot[0].id"
                  size="16"
                  aria-hidden="true"
                />
                <span v-else class="timeslots-mobile__selection__body__slot--suffix">
                  ({{ timeslot.length }})
                </span>
              </button>
            </template>
            <TimeslotsDropdown :timeslots="timeslot" @dropdownSelection="handleSelectTimeslot" />
          </el-popover>
          <div v-else class="timeslots-mobile__selection__body__radio-wrapper">
            <input
              type="radio"
              :id="`time-${timeslot.id}`"
              :name="'timeslot-' + singleDate"
              class="sr-only"
              :value="timeslot"
              :checked="isSelectedTimeslot(timeslot)"
              @change="handleSelectTimeslot(timeslot)"
              :aria-invalid="isOverlappingTimeslot(timeslot)"
              :aria-describedby="
                isOverlappingTimeslot(timeslot) ? `time-${timeslot.id}-error` : null
              "
            />
            <label
              :for="`time-${timeslot.id}`"
              class="timeslots-mobile__selection__body__slot"
              :class="{
                'timeslots-mobile__selection__body__slot--selected': isSelectedTimeslot(timeslot),
                'timeslots-mobile__selection__body__slot--error': isOverlappingTimeslot(timeslot),
              }"
            >
              <IconCheck v-if="isSelectedTimeslot(timeslot)" aria-hidden="true" />
              {{ minutesToHHMM(timeslot.von) }}
              <span class="sr-only">Uhr, {{ convertDateHeader }}</span>
            </label>
            <span
              v-if="isOverlappingTimeslot(timeslot)"
              class="sr-only"
              :id="`time-${timeslot.id}-error`"
            >
              Es gibt bereits einen Termin zu dieser Zeit.
            </span>
          </div>
        </template>
      </fieldset>
      <div
        class="timeslots-mobile__selection__empty"
        v-if="!singleDateTimeslots.length"
        role="status"
        aria-live="polite"
      >
        <IconCalendar color="#737380" aria-hidden="true" />
        <p class="timeslots-mobile__empty__text">
          Leider keine verfügbaren Termine in den angezeigten Tagen.<br />
          <span class="timeslots-mobile__empty__text--highlight"
            >Bitte öffnen Sie den Kalender und wählen Sie dort einen grau hinterlegten Tag</span
          ><br />
          oder wählen Sie einen anderen Stützpunkt.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { clusterTimeslots, getLastSunday, getNextDate, minutesToHHMM } from '@/utils/format.js'
import { mapGetters } from 'vuex'

import TimeslotsDropdown from '@/components/Booking/TimeslotsDropdown.vue'
import IconCalendar from '@/components/icons/IconCalendar.vue'
import IconCheck from '@/components/icons/IconCheck.vue'
import IconChevronUp from '@/components/icons/IconChevronUp.vue'

export default {
  components: { IconCalendar, IconChevronUp, TimeslotsDropdown, IconCheck },
  props: {
    dayFilters: {
      type: Array,
    },
    isDayFiltersSet: {
      type: Boolean,
      default: false,
    },
    filteredTimeslots: {
      type: Array,
      default: () => [],
    },
    overlappingTimeslots: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeDropdown: null,
    }
  },
  computed: {
    ...mapGetters(['singleDate', 'lastTimeslot', 'selectedTimeslot']),
    convertDateHeader() {
      const weekday = new Date(this.singleDate).toLocaleDateString('de-AT', {
        weekday: 'long',
      })
      const dateMonth = new Date(this.singleDate).toLocaleDateString('de-AT', {
        day: '2-digit',
        month: 'long',
      })
      return `${weekday}, ${dateMonth}`
    },
    dateBubbles() {
      const lastSunday = getLastSunday(this.singleDate)
      const firstDateOfWeekBubbles = getNextDate(lastSunday, 1)
      const lastDateOfWeekBubbles = getNextDate(lastSunday, 27)

      if (this.isDayFiltersSet) {
        let dateBubblesArray = [[]]
        let date = lastSunday
        let week = 0
        let day = 1

        while (date < lastDateOfWeekBubbles) {
          if (day === 0) {
            date = getNextDate(date, 1)
            week += 1
            dateBubblesArray[week] = []
            day += 1
          } else {
            date = getNextDate(date, 1)
            if (this.dayFilters[day - 1].value) dateBubblesArray[week].push(date)
            day === 6 ? (day = 0) : (day += 1)
          }
        }

        return dateBubblesArray
      } else {
        let dateBubblesArray = [[firstDateOfWeekBubbles]]
        let date = firstDateOfWeekBubbles
        let week = 0

        while (date < lastDateOfWeekBubbles) {
          if (dateBubblesArray[week].length < 6) {
            date = getNextDate(date, 1)
            dateBubblesArray[week].push(date)
          } else {
            date = getNextDate(date, 2)
            week += 1
            dateBubblesArray[week] = []
            dateBubblesArray[week].push(date)
          }
        }
        // [['2022-03-21', ''], ['2022-03-28', '']...] etc
        return dateBubblesArray
      }
    },
    availableDates() {
      return [...new Set(this.filteredTimeslots.map((timeslot) => timeslot.datum))]
    },
    // filteredTimeslots > unstackedTimeslots > singleDateTimeslots > clusteredTimeslots
    unstackedTimeslots() {
      // show only first timeslot if service is stacked
      return this.filteredTimeslots.filter((timeslot) => timeslot.sequenceNumber !== 2)
    },
    singleDateTimeslots() {
      return this.unstackedTimeslots.filter((timeslot) => timeslot.datum === this.singleDate)
    },
    clusteredTimeslots() {
      return clusterTimeslots(this.singleDateTimeslots)
    },
  },
  watch: {
    lastTimeslot(newValue, oldValue) {
      // when API finish fetching
      if (oldValue && !newValue) {
        if (!this.availableDates.includes(this.singleDate)) {
          this.$store.commit('setSingleDate', this.availableDates[0])
        }
      }
    },
    dateBubbles() {
      const uniqueDates = [...new Set(this.dateBubbles.flat().map((date) => date.slice(0, -3)))]
      this.$emit('monthYearArray', uniqueDates)
    },
  },
  methods: {
    minutesToHHMM,
    convertToDate(date) {
      return new Date(date).getDate().toLocaleString('de-AT', { minimumIntegerDigits: 2 })
    },
    convertToDay(date) {
      return new Date(date).toLocaleDateString('de-AT', {
        weekday: 'short',
      })
    },
    isDropdownSelected(timeslot) {
      return (
        this.selectedTimeslot &&
        timeslot.map((slot) => slot.datum).includes(this.selectedTimeslot[0]?.datum) &&
        timeslot.map((slot) => slot.von).includes(this.selectedTimeslot[0]?.von)
      )
    },
    isOverlappingTimeslot(timeslot) {
      return this.overlappingTimeslots.some(
        (overlappingTimeslot) =>
          overlappingTimeslot.datum === timeslot.datum && overlappingTimeslot.von === timeslot.von
      )
    },
    isSelectedTimeslot(timeslot) {
      return (
        this.selectedTimeslot &&
        this.selectedTimeslot[0]?.datum === timeslot.datum &&
        this.selectedTimeslot[0]?.von === timeslot.von
      )
    },
    handleSelectDate(date) {
      this.$store.commit('setSingleDate', date)
      this.$refs.calendarRef.scrollLeft = 0
    },
    handleSelectTimeslot(timeslot) {
      if (timeslot.stackId) {
        const timeslots = this.filteredTimeslots.filter(
          (element) => element.stackId === timeslot.stackId
        )
        this.$store.commit('setSelectedTimeslot', timeslots)
      } else {
        this.$store.commit('setSelectedTimeslot', [timeslot])
      }
    },
    getDateAriaLabel(date) {
      const dayName = new Date(date).toLocaleDateString('de-AT', {
        weekday: 'long',
      })
      const dayMonth = new Date(date).toLocaleDateString('de-AT', {
        day: '2-digit',
        month: 'long',
      })
      const isAvailable = this.availableDates.includes(date)
      const isSelected = date === this.singleDate

      let status = ''
      if (isSelected) status = ', ausgewählt'
      if (!isAvailable) status = ', keine Termine verfügbar'

      return `${dayName}, ${dayMonth}${status}`
    },
  },
}
</script>

<style lang="scss">
.timeslots-mobile {
  &__calendar {
    display: flex;
    padding-bottom: 16px;
    overflow: scroll;

    &__week {
      display: flex;
      gap: 16px;

      hr {
        margin-top: 16px;
        margin-right: 16px;
        width: 40px;
      }
    }

    &__date {
      &__digit {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        cursor: pointer;
        height: 32px;
        width: 32px;
        border: 1px solid $color-grey-dark;
        background-color: white;
        color: $color-grey-darker;
        font-weight: $font-weight-medium;
        margin-bottom: 4px;

        &--available {
          background-color: $color-text-secondary;
          border-color: $color-text-secondary;
          color: $color-text-white;
        }

        &--selected#{&}--available {
          background-color: $color-black;
          border-color: $color-black;
          color: $color-text-white;
        }

        &--selected:not(&--available) {
          border-color: $color-black;
          color: $color-black;
        }
      }

      &__day {
        font-size: 12px;
        text-align: center;
        text-transform: uppercase;
      }
    }
  }

  &__selection {
    &__header {
      background-color: $color-black;
      border-radius: 4px;
      color: white;
      font-weight: $font-weight-bold;
      margin-bottom: 8px;
      padding: 8px 0;
      text-align: center;
    }

    &__body {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 4px;
      font-weight: $font-weight-medium;

      .el-popper__arrow {
        display: none;
      }

      .timeslots-dropdown {
        font-size: 16px;
      }

      &__slot {
        background-color: rgba($color-grey-light, 0.7);
        border-radius: 4px;
        cursor: pointer;
        flex-basis: 32%;
        padding: 12px 0;
        text-align: center;

        &:hover:not(&--selected) {
          background-color: $color-grey-dark;
        }

        &--active {
          background-color: $color-grey-dark;
        }

        &--selected {
          background-color: $color-yellow-primary;
        }

        &--error {
          border: 1px solid $color-danger-primary;
        }

        &--prefix,
        &--suffix {
          color: $color-text-secondary;
          font-size: 12px;
          font-weight: $font-weight-regular;
        }

        svg {
          vertical-align: middle;
        }
      }

      &__radio-wrapper {
        display: flex;
        flex-direction: column;

        input:focus-visible + label {
          outline: 2px solid $color-info-primary;
          outline-offset: 2px;
        }
      }

      button.timeslots-mobile__selection__body__slot:focus-visible {
        outline: 2px solid $color-info-primary;
        outline-offset: 2px;
      }
    }

    &__empty {
      @include flex-center;
      flex-direction: column;
      background-color: rgba($color-grey-light, 0.3);
      border-radius: 4px;
      color: $color-text-secondary;
      height: 200px;
      padding: 16px;
      text-align: center;

      svg {
        margin-bottom: 12px;
      }

      &__text {
        max-width: 300px;

        &--highlight {
          font-weight: $font-weight-medium;
        }
      }
    }
  }
}
</style>
